/** @typedef {{ time: number, bpm: number, meter: number }} TimingPoint */
/** @typedef {{ x: number, y: number }} Pt */
/** @typedef {{ cmd:'M'|'L', x:number, y:number } | { cmd:'B', cx:number, cy:number, x:number, y:number } } PathCommand */

const FLATTEN_STEP_PX = 4; // target sampling resolution
const MAX_SAMPLES_PER_SEG = 256;

const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

function distanceMap(points) {
  const map = [0];
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    map.push(map[i - 1] + Math.hypot(dx, dy));
  }
  return map;
}

// Quadratic Bézier point
function quadPoint(p0, p1, p2, t) {
  const u = 1 - t;
  const a = u * u,
    b = 2 * u * t,
    c = t * t;
  return { x: a * p0.x + b * p1.x + c * p2.x, y: a * p0.y + b * p1.y + c * p2.y };
}

// Catmull–Rom (centripetal) evaluation between P1..P2
function catmullRomPoint(p0, p1, p2, p3, t, t0, t1, t2, t3) {
  // De Casteljau–style with chord-time parameterization
  const lerp = (A, B, ta, tb, t) => {
    const w = (t - ta) / (tb - ta);
    return { x: A.x + w * (B.x - A.x), y: A.y + w * (B.y - A.y) };
  };
  const A1 = lerp(p0, p1, t0, t1, t);
  const A2 = lerp(p1, p2, t1, t2, t);
  const A3 = lerp(p2, p3, t2, t3, t);
  const B1 = lerp(A1, A2, t0, t2, t);
  const B2 = lerp(A2, A3, t1, t3, t);
  const C = lerp(B1, B2, t1, t2, t);
  return C;
}

// Sample a centripetal Catmull–Rom spline through all control points
function sampleCatmullRom(points, stepPx = FLATTEN_STEP_PX, alpha = 0.5) {
  const n = points.length;
  if (n < 2) return points.slice();
  const out = [];

  for (let i = 0; i < n - 1; i++) {
    const p0 = i === 0 ? points[0] : points[i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = i + 2 < n ? points[i + 2] : points[n - 1];

    // chord-length times
    const t0 = 0;
    const t1 = t0 + Math.pow(d(p0, p1), alpha);
    const t2 = t1 + Math.pow(d(p1, p2), alpha);
    const t3 = t2 + Math.pow(d(p2, p3), alpha);

    const segLen = Math.max(1, d(p1, p2));
    const m = Math.max(2, Math.min(MAX_SAMPLES_PER_SEG, Math.ceil(segLen / stepPx)));

    for (let k = 0; k <= m; k++) {
      // avoid duplicating the join point (k=0) except for the very first segment
      if (i > 0 && k === 0) continue;
      const u = k / m;
      const tt = t1 + u * (t2 - t1);
      out.push(catmullRomPoint(p0, p1, p2, p3, tt, t0, t1, t2, t3));
    }
  }
  return out;
}

function flattenPoints(points) {
  if (points.length <= 2) return points.slice();
  if (points.length === 3) {
    // treat [p0,ctrl,p1] as a quadratic Bézier
    const [p0, p1, p2] = points;
    const chord = d(p0, p2);
    const segs = Math.max(16, Math.min(256, Math.ceil(chord / FLATTEN_STEP_PX)));
    const out = new Array(segs + 1);
    for (let i = 0; i <= segs; i++) out[i] = quadPoint(p0, p1, p2, i / segs);
    return out;
  }
  // 4+ points → centripetal Catmull–Rom spline sampling
  return sampleCatmullRom(points, FLATTEN_STEP_PX, 0.5);
}

/**
 * Build a path table providing time→distance mapping for a polyline or quad curve.
 * Automatically reverses on odd repeats.
 *
 * @param {{x:number,y:number}[]} points
 * @param {number} duration
 * @param {number} [repeats=1]
 * @returns {{ points: {x:number,y:number}[], distances: number[], totalLength: number, repeats: number, duration: number, timeToDistance: (t:number)=>number, pointAt:(t:number)=>{x:number,y:number} }}
 */
export function buildPathTable(points, duration, repeats = 1, alreadyFlat = false) {
  repeats = Math.max(1, Number(repeats) || 0);

  // *** important: flatten curves to a dense polyline ***
  const samples = alreadyFlat ? points.slice() : flattenPoints(points);

  const distances = distanceMap(samples);
  const totalLength = distances[distances.length - 1] || 0;
  const runDuration = duration / repeats;

  function timeToDistance(t) {
    const run = Math.max(0, Math.min(Math.floor(t / runDuration), repeats - 1));
    const localT = (t - run * runDuration) / runDuration;
    const dist = totalLength * localT;
    return run % 2 === 0 ? dist : totalLength - dist;
  }

  function pointAt(t) {
    const d = Math.min(timeToDistance(t), totalLength);
    if (d >= totalLength) return samples[samples.length - 1];
    let i = distances.findIndex((v) => v > d);
    if (i === -1) i = distances.length - 2; else i -= 1;
    if (i >= samples.length - 1) return samples[samples.length - 1];
    const segStart = distances[i];
    const segEnd = distances[i + 1];
    const ratio = segEnd === segStart ? 0 : (d - segStart) / (segEnd - segStart);
    const p0 = samples[i];
    const p1 = samples[i + 1];
    return { x: p0.x + (p1.x - p0.x) * ratio, y: p0.y + (p1.y - p0.y) * ratio };
  }

  return { points: samples, distances, totalLength, repeats, duration, timeToDistance, pointAt };
}

/**
 * Compute the slider end time accounting for SV and BPM changes.
 * ``pixelLength`` is the length of the slider in pixels and ``sv`` is the
 * slider velocity (px per beat / 100 in circle terms). ``timing`` must be
 * sorted by time.
 *
 * @param {number} tStart
 * @param {number} pixelLength
 * @param {number} sv
 * @param {TimingPoint[]} timing
 * @returns {number}
 */
export function computeEndTime(tStart, pixelLength, sv, timing) {
  const beatsNeeded = pixelLength / (100 * sv);
  let beatsRemaining = beatsNeeded;
  let t = tStart;

  const bpmAt = (time) => {
    let current = timing[0]?.bpm ?? 120;
    for (const change of timing) {
      if (time >= change.time) current = change.bpm; else break;
    }
    return current;
  };

  let idx = timing.findIndex((c) => c.time > t);
  if (idx === -1) idx = timing.length;
  let bpm = bpmAt(t);

  while (beatsRemaining > 0) {
    const nextChange = timing[idx];
    const beatDur = 60000 / bpm;
    if (!nextChange) {
      t += beatsRemaining * beatDur;
      break;
    }
    const timeUntilChange = nextChange.time - t;
    const beatsUntilChange = timeUntilChange / beatDur;
    if (beatsRemaining <= beatsUntilChange) {
      t += beatsRemaining * beatDur;
      break;
    }
    t = nextChange.time;
    beatsRemaining -= beatsUntilChange;
    bpm = nextChange.bpm;
    idx += 1;
  }

  return t;
}

/**
 * Generate tick times at beat divisions over the slider duration.
 *
 * @param {number} tStart
 * @param {number} duration
 * @param {number} beatDivisor
 * @param {number} [repeats=1]
 * @param {TimingPoint[]} timing
 * @returns {number[]}
 */
export function generateTicks(tStart, duration, beatDivisor, repeats = 1, timing) {
  repeats = Math.max(1, Number(repeats) || 0);
  const ticks = [];
  const runDuration = duration / repeats;

  const bpmAt = (time) => {
    let current = timing[0]?.bpm ?? 120;
    for (const change of timing) {
      if (time >= change.time) current = change.bpm; else break;
    }
    return current;
  };

  const nextChangeAfter = (time) => {
    for (const change of timing) {
      if (change.time > time) return change.time;
    }
    return null;
  };

    for (let r = 0; r < repeats; r++) {
      const runStart = tStart + r * runDuration;
      const runEnd = runStart + runDuration;
      let t = runStart;
      for (;;) {
        const bpm = bpmAt(t);
        const interval = 60000 / bpm / beatDivisor;
        const nextTick = t + interval;
        const nextChange = nextChangeAfter(t);
        if (nextChange !== null && nextChange < nextTick) {
          t = nextChange;
          continue;
        }
        if (nextTick >= runEnd - 0.001) break;
        ticks.push(nextTick - tStart);
        t = nextTick;
      }
      if (r < repeats - 1) ticks.push((r + 1) * runDuration);
    }
    return ticks;
  }

/**
 * Decode a list of path command strings into structured commands. Supported
 * commands:
 *   ``M x y`` – move to ``(x,y)``
 *   ``L x y`` – line to ``(x,y)``
 *   ``B cx cy x y`` – quadratic Bézier with control ``(cx,cy)`` ending at
 *     ``(x,y)``. Multiple Bézier segments may be packed into one ``B`` command
 *     string.
 * The decoded sequence preserves the original command order without performing
 * any tessellation.
 *
 * @param {string[]} commands
 * @returns {PathCommand[]}
 */
export function decodePathCommands(commands) {
  /** @type {PathCommand[]} */
  const out = [];
  if (!Array.isArray(commands)) return out;
  for (const cmd of commands) {
    const parts = String(cmd).trim().split(/\s+/);
    const type = parts.shift();
    const nums = parts.map(Number);
    if (type === 'M' || type === 'L') {
      out.push({ cmd: /** @type {'M'|'L'} */ (type), x: nums[0] ?? 0, y: nums[1] ?? 0 });
    } else if (type === 'B') {
      for (let i = 0; i + 3 < nums.length; i += 4) {
        out.push({
          cmd: 'B',
          cx: nums[i] ?? 0,
          cy: nums[i + 1] ?? 0,
          x: nums[i + 2] ?? 0,
          y: nums[i + 3] ?? 0,
        });
      }
    }
  }
  return out;
}

/**
 * Convert structured path commands into command strings.
 *
 * @param {PathCommand[]} commands
 * @returns {string[]}
 */
export function commandsToPath(commands) {
  const out = [];
  for (const c of commands) {
    if (c.cmd === 'M' || c.cmd === 'L') {
      out.push(`${c.cmd} ${c.x} ${c.y}`);
    } else if (c.cmd === 'B') {
      out.push(`B ${c.cx} ${c.cy} ${c.x} ${c.y}`);
    }
  }
  return out;
}

/**
 * Tessellate structured path commands into a dense polyline. Lines are sampled
 * at roughly ``stepPx`` spacing while Bézier segments use quadratic sampling
 * with a similar resolution. The resulting array includes the starting point of
 * the path.
 *
 * @param {PathCommand[]} commands
 * @param {number} [stepPx=FLATTEN_STEP_PX]
 * @returns {{x:number,y:number}[]} Polyline of sampled points.
 */
export function tessellateCommands(commands, stepPx = FLATTEN_STEP_PX) {
  /** @type {{x:number,y:number}[]} */
  const pts = [];
  if (!Array.isArray(commands)) return pts;
  let prev = null;
  for (const c of commands) {
    if (c.cmd === 'M') {
      prev = { x: c.x, y: c.y };
      pts.push({ x: c.x, y: c.y });
    } else if (c.cmd === 'L') {
      if (!prev) {
        prev = { x: c.x, y: c.y };
        pts.push(prev);
        continue;
      }
      const end = { x: c.x, y: c.y };
      const segLen = Math.max(1, Math.hypot(end.x - prev.x, end.y - prev.y));
      const steps = Math.max(1, Math.min(MAX_SAMPLES_PER_SEG, Math.ceil(segLen / stepPx)));
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        pts.push({
          x: prev.x + (end.x - prev.x) * t,
          y: prev.y + (end.y - prev.y) * t,
        });
      }
      prev = end;
    } else if (c.cmd === 'B') {
      if (!prev) {
        prev = { x: c.x, y: c.y };
        pts.push(prev);
        continue;
      }
      const ctrl = { x: c.cx, y: c.cy };
      const end = { x: c.x, y: c.y };
      const chord = Math.hypot(end.x - prev.x, end.y - prev.y);
      const segs = Math.max(16, Math.min(256, Math.ceil(chord / stepPx)));
      const curve = tessellateQuadratic(prev, ctrl, end, chord / segs);
      pts.push(...curve.slice(1)); // avoid duplicate start
      prev = end;
    }
  }
  return pts;
}


/**
 * @typedef {{ tMs: number, x: number, y: number }} ClickPoint
 */

/**
 * @typedef {{ startMs: number, endMs: number, points: Array<{x:number,y:number,tMs:number}> }} PathSegment
 */

const DEFAULT_RESAMPLE_POINTS = 16;

/**
 * Simplify a series of points by removing sharp angle samples. This helps smooth
 * out pointer jitter (e.g. axis-aligned updates during a diagonal swipe) before
 * Catmull-Rom resampling.
 *
 * @param {{x:number,y:number,tMs?:number}[]} pts
 * @param {number} [angleThreshold=Math.PI * 0.75] Minimum angle (radians) to keep a point.
 * @returns {{x:number,y:number,tMs?:number}[]}
 */
function simplifyByAngle(pts, angleThreshold = Math.PI * 0.75) {
  if (!Array.isArray(pts) || pts.length <= 2) return pts.slice();
  const res = [pts[0]];
  let prevIdx = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[prevIdx];
    const cur = pts[i];
    const next = pts[i + 1];
    const v1x = cur.x - prev.x;
    const v1y = cur.y - prev.y;
    const v2x = next.x - cur.x;
    const v2y = next.y - cur.y;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);
    if (len1 === 0 || len2 === 0) continue; // skip duplicate points
    const cos = (v1x * v2x + v1y * v2y) / (len1 * len2);
    const angle = Math.acos(Math.min(1, Math.max(-1, cos)));
    if (angle >= angleThreshold) {
      res.push(cur);
      prevIdx = i;
    }
    // otherwise skip cur to smooth sharp corner
  }
  res.push(pts[pts.length - 1]);
  return res;
}

function pathLength(pts) {
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  }
  return len;
}

/**
 * Link consecutive click points into path segments. Consecutive points further apart than
 * ``threshold`` start a new segment. Each segment is smoothed with a Catmull-Rom spline and
 * resampled to ``RESAMPLE_POINTS`` points with linearly interpolated timestamps.
 *
 * @param {ClickPoint[]} points
 * @param {number} smoothing Value in [0,1] controlling Catmull-Rom tension (0 = smoothest).
 * @param {number} [threshold=1] Distance threshold to split segments.
 * @returns {PathSegment[]} Array of path segments.
 */
export function linkPaths(points, smoothing, threshold = 1, samples = DEFAULT_RESAMPLE_POINTS) {
  if (!Array.isArray(points) || points.length < 2) {
    return [];
  }

  const pts = points.map((p) => ({ x: p.x, y: p.y, tMs: p.tMs }));
  const segments = [];
  let current = [pts[0]];

  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const dist = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    if (dist > threshold) {
      if (current.length > 1) segments.push(current);
      current = [cur];
    } else {
      current.push(cur);
    }
  }
  if (current.length > 1) segments.push(current);

  return segments.map((seg) => {
    const simplified = simplifyByAngle(seg);
    const count = Math.max(2, samples);
    const resampled = resampleCatmullRom(simplified, smoothing, count);
    const startMs = simplified[0].tMs;
    const endMs = simplified[simplified.length - 1].tMs;
    const duration = endMs - startMs;
    const pts = resampled.map((p, i) => ({
      x: p.x,
      y: p.y,
      tMs: startMs + (duration * i) / (count - 1),
    }));
    return { startMs, endMs, points: pts };
  });
}

/**
 * Resample an array of points using a Catmull-Rom spline.
 *
 * @param {{x:number,y:number}[]} points
 * @param {number} smoothing Value in [0,1] controlling tension.
 * @param {number} samples Number of points in the resulting segment.
 * @returns {{x:number,y:number}[]}
 */
function resampleCatmullRom(points, smoothing, samples) {
  const result = [];
  const n = points.length;
  if (n < 2) return points.slice();
  const t = Math.max(0, Math.min(1, smoothing));
  for (let i = 0; i < samples; i++) {
    const u = (i / (samples - 1)) * (n - 1);
    const idx = Math.floor(u);
    const localT = u - idx;
    const p0 = points[idx - 1] || points[idx];
    const p1 = points[idx];
    const p2 = points[idx + 1] || points[idx];
    const p3 = points[idx + 2] || p2;
    result.push(catmullRom(p0, p1, p2, p3, localT, t));
  }
  return result;
}

/**
 * Catmull-Rom spline calculation between ``p1`` and ``p2``.
 * ``tension`` is in [0,1] where 0 is Catmull-Rom and 1 approaches straight lines.
 *
 * @param {{x:number,y:number}} p0
 * @param {{x:number,y:number}} p1
 * @param {{x:number,y:number}} p2
 * @param {{x:number,y:number}} p3
 * @param {number} t Parameter along the segment [0,1].
 * @param {number} tension
 * @returns {{x:number,y:number}}
 */
function catmullRom(p0, p1, p2, p3, t, tension) {
  const t2 = t * t;
  const t3 = t2 * t;
  const s = (1 - tension) / 2; // 0 => Catmull-Rom, 1 => linear

  const m1x = s * (p2.x - p0.x);
  const m1y = s * (p2.y - p0.y);
  const m2x = s * (p3.x - p1.x);
  const m2y = s * (p3.y - p1.y);

  const a = 2 * t3 - 3 * t2 + 1;
  const b = t3 - 2 * t2 + t;
  const c = -2 * t3 + 3 * t2;
  const d = t3 - t2;

  return {
    x: a * p1.x + b * m1x + c * p2.x + d * m2x,
    y: a * p1.y + b * m1y + c * p2.y + d * m2y,
  };
}

/**
 * Encode a polyline into simple path commands. Currently only ``M`` (move)
 * and ``L`` (line) commands are generated. This mirrors the slider path
 * encoding used by circle-mode beatmaps.
 *
 * @param {{x:number,y:number}[]} points
 * @returns {string[]} Array of command strings.
 */
function encodePathCommands(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const cmds = [`M ${points[0].x} ${points[0].y}`];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    // Represent each segment as a quadratic Bézier using the start point as the
    // control point. This preserves linear runs while ensuring all encoded
    // paths contain Bézier commands for downstream consumers that expect them.
    cmds.push(`B ${prev.x} ${prev.y} ${cur.x} ${cur.y}`);
  }
  return cmds;
}

/**
 * Tessellate a quadratic Bézier curve into a polyline.
 *
 * @param {{x:number,y:number}} p0 Start point
 * @param {{x:number,y:number}} p1 Control point
 * @param {{x:number,y:number}} p2 End point
 * @param {number} [resolution=1] Approximate spacing between samples
 * @returns {{x:number,y:number}[]} Array of points including ``p0`` and ``p2``.
 */
function tessellateQuadratic(p0, p1, p2, resolution = 1) {
  const len =
    Math.hypot(p1.x - p0.x, p1.y - p0.y) +
    Math.hypot(p2.x - p1.x, p2.y - p1.y);
  const steps = Math.max(2, Math.ceil(len / Math.max(resolution, 1e-6)));
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const mt = 1 - t;
    pts.push({
      x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
      y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
    });
  }
  return pts;
}

/**
 * Convert raw pointer samples into circle-style path commands while estimating
 * the number of repeats based on direction reversals. Only the first run of
 * the slider path is encoded; additional runs are represented via the repeat
 * count.
 *
 * @param {ClickPoint[]} points
 * @returns {{ path: string[], repeats: number }}
 */
export function samplesToPath(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return { path: [], repeats: 1 };
  }

  const MIN_STEP = 0.01; // ignore jitter smaller than a pixel

  let dir: { x: number; y: number } | null = null;
  let prevDot: number | null = null;
  let repeats = 1;
  let splitIdx = points.length;

  for (let i = 1; i < points.length; i++) {
    const step = {
      x: points[i].x - points[i - 1].x,
      y: points[i].y - points[i - 1].y,
    };
    const len = Math.hypot(step.x, step.y);
    if (len < MIN_STEP) continue; // skip tiny steps at start or within path
    if (!dir) {
      dir = step;
      prevDot = null;
      continue;
    }
    const dot = step.x * dir.x + step.y * dir.y;
    if (prevDot !== null && dot * prevDot < 0) {
      repeats += 1;
      if (repeats === 2) splitIdx = i; // end of first run
    }
    prevDot = dot;
  }

  const base = points.slice(0, splitIdx);
  let poly;

  // Detect near-linear gestures so they can be converted into a noticeable arc.
  const isStraight = () => {
    if (base.length <= 2) return true;
    const p0 = base[0];
    const p1 = base[base.length - 1];
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) return false;
    let maxDev = 0;
    for (let i = 1; i < base.length - 1; i++) {
      const px = base[i].x - p0.x;
      const py = base[i].y - p0.y;
      const dev = Math.abs(px * dy - py * dx) / dist; // perpendicular distance
      if (dev > maxDev) maxDev = dev;
    }
    return maxDev / dist < 0.1; // within 10% of line length
  };

  if (isStraight()) {
    const p0 = base[0];
    const p1 = base[base.length - 1];
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 2) {
      poly = [p0, p1];
    } else {
      const mid = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
      const nx = -dy / dist;
      const ny = dx / dist;
      const offset = Math.max(1, dist * 0.3);
      const cand1 = { x: mid.x + nx * offset, y: mid.y + ny * offset };
      const cand2 = { x: mid.x - nx * offset, y: mid.y - ny * offset };
      const inBounds = (p) => p.x >= 0 && p.x <= 9 && p.y >= 0 && p.y <= 16;
      let ctrl = cand1;
      const margin = (p) => Math.min(p.x, 9 - p.x, p.y, 16 - p.y);
      if (!inBounds(cand1) && inBounds(cand2)) {
        ctrl = cand2;
      } else if (inBounds(cand1) && inBounds(cand2)) {
        ctrl = margin(cand2) > margin(cand1) ? cand2 : cand1;
      }
      const steps = Math.max(16, Math.ceil(dist / 4) + 1);
      const res = dist / (steps - 1);
      poly = tessellateQuadratic(p0, ctrl, p1, res);
    }
  } else {
    const len = pathLength(base);
    const samples = Math.max(16, Math.ceil(len / 4) + 1);
    const seg = linkPaths(base, 0, 1, samples)[0];
    poly = seg ? seg.points.map((p) => ({ x: p.x, y: p.y })) : base;
  }
  return { path: encodePathCommands(poly), repeats, points: poly };
}